<template>
  <div>
    <v-form ref="orderForm" lazy-validation>
      <v-row>
        <v-col cols="6" class="pt-2">
          <FoSessionOption v-if="foParams.widgets.foSessionOption" />
          <div v-if="selectedOrderSession.is_photogrammetry">
            <PhotogrammetrySystem
              v-if="foParams.photogrammetryWidgets.photogrammetrySystem"
              v-model="localForwardedOrderForm.order_form.photogrammetry_system"
            />
            <PhotogrammetryLibExport
              v-if="foParams.photogrammetryWidgets.photogrammetryLibraryExport"
              v-model="
                localForwardedOrderForm.order_form.photogrammetry_library_export
              "
            />
            <PhotogrammetryHealingCaps
              v-if="foParams.photogrammetryWidgets.photogrammetryHealingCaps"
              v-model="
                localForwardedOrderForm.order_form
                  .photogrammetry_tissue_scan_healing_cap
              "
            />
          </div>
          <BarAfterDesign
            v-if="foParams.widgets.barAfterDesign"
            v-model="localForwardedOrderForm.order_form.bar_after_design"
          />
          <ConnectionTypeSelector
            v-if="foParams.widgets.connectionTypeSelector"
            v-model="localForwardedOrderForm.order_form.connection_type"
          />
          <BarHighShineAddon
            v-if="
              currentTenant.bar_high_shine_enabled &&
              this.foParams.product === 'BAR' &&
              this.acquisitionMethod !== 'quota'
            "
            v-model="localForwardedOrderForm.order_form.bar_high_shine"
          />
          <v-scale-transition origin="center center">
            <v-card-text v-if="!!checkImplantsError">
              <v-alert color="error" dark class="text-center mb-0 rounded-10">
                <v-icon class="mr-2"> mdi-alert </v-icon>
                {{ checkImplantsError }}
              </v-alert>
            </v-card-text>
          </v-scale-transition>
          <MultiUnitConfirmation
            v-if="foParams.widgets.multiUnitConfirmation"
            v-model="localForwardedOrderForm.implants"
            :interactive="true"
            :validate-form="foParams.caseType === 'direct'"
          />
          <SpecialRequests
            v-if="foParams.widgets.specialRequests"
            v-model="localForwardedOrderForm.special_requests"
          />
          <ClientOrderForm
            v-if="foParams.widgets.clientOrderForm"
            v-model="localForwardedOrderForm.order_form"
          />
        </v-col>
        <v-col cols="6" class="d-flex flex-column pt-2">
          <UploadProjectFiles
            v-if="foParams.widgets.uploadProjectFiles"
            :foParams="foParams"
            v-model="files"
          />

          <v-card-actions class="mt-auto">
            <v-spacer />
            <v-btn
              class="rounded-10 btn-error"
              elevation="0"
              @click="$emit('back')"
            >
              Go Back
            </v-btn>
            <v-btn
              class="btn-primary my-4 mx-4"
              @click="handleContinue"
              :disabled="
                (!isOwner && !designsAvailable) || checkImplantsError !== null
              "
            >
              {{
                foParams.caseType === "handover"
                  ? "Continue Order"
                  : "Send Order"
              }}
            </v-btn>
          </v-card-actions>
        </v-col>
      </v-row>
    </v-form>

    <PurchaseDialog
      v-model="dialog"
      :item="lineItem"
      :strategy="acquisitionStrategy"
      :totalPriceCents="totalPriceCents"
      :totalTaxAmount="totalTaxAmount"
      :taxInclusive="taxInclusive"
      :acquisitionMethod="acquisitionMethod"
      :customerBalance="customerBalance"
      :loading="loading"
      @confirm="handleConfirm"
      @reloadAcquisitionStrategy="loadAcquisitionStrategy"
    />
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex"
import { deepCopy } from "@/utils"
import client from "@/lib/ApiClient"
import MultiUnitConfirmation from "@/components/shared/MultiUnitConfirmation.vue"
import PurchaseDialog from "@/components/shared/PurchaseDialog.vue"
import ConnectionTypeSelector from "./ForwardedOrderOptions/ConnectionTypeSelector.vue"
import UploadProjectFiles from "./ForwardedOrderOptions/UploadProjectFiles.vue"
import SpecialRequests from "@/components/shared/SpecialRequests.vue"
import FoSessionOption from "./ForwardedOrderOptions/FoSessionOption.vue"
import ClientOrderForm from "./ForwardedOrderOptions/ClientOrderForm.vue"
import BarAfterDesign from "./ForwardedOrderOptions/BarAfterDesign.vue"
import PhotogrammetrySystem from "./ForwardedOrderOptions/PhotogrammetrySystem.vue"
import PhotogrammetryHealingCaps from "./ForwardedOrderOptions/PhotogrammetryHealingCaps.vue"
import PhotogrammetryLibExport from "./ForwardedOrderOptions/PhotogrammetryLibExport.vue"
import BarHighShineAddon from "./ForwardedOrderOptions/BarHighShineAddon.vue"

export default {
  name: "ForwardedOrderOptions",

  components: {
    PurchaseDialog,
    ConnectionTypeSelector,
    MultiUnitConfirmation,
    UploadProjectFiles,
    SpecialRequests,
    FoSessionOption,
    ClientOrderForm,
    BarAfterDesign,
    PhotogrammetrySystem,
    PhotogrammetryHealingCaps,
    PhotogrammetryLibExport,
    BarHighShineAddon
  },

  props: ["foParams"],

  data() {
    return {
      dialog: false,
      loading: false,
      acquisitionStrategy: null,
      totalPriceCents: null,
      totalTaxAmount: null,
      taxInclusive: null,
      customerBalance: null,
      localForwardedOrderForm: {},
      clientOrderForm: {},
      files: [],
      retryCount: 0
    }
  },

  watch: {
    localForwardedOrderForm: {
      async handler(value) {
        if (!value || !this.selectedForwardedOrder) return
        const response = await client.instance.orders.updateForwardedOrder(
          this.selectedOrder.uid,
          this.selectedOrderSession.uid,
          this.selectedForwardedOrder.uid,
          value
        )
        this.setSelectedForwardedOrder(response)

        const orderForwardedOrdersCopy = deepCopy(this.orderForwardedOrders)
        const foIndex = orderForwardedOrdersCopy.findIndex(
          (fo) => fo.uid === this.selectedForwardedOrder.uid
        )
        orderForwardedOrdersCopy.splice(foIndex, 1, response)
        this.setOrderForwardedOrders(orderForwardedOrdersCopy)
      },
      deep: true
    },

    files: {
      handler(value) {
        this.localForwardedOrderForm.file_uids = value
          .filter((item) => item !== null)
          .map((item) => item.uid)
      },
      deep: true
    }
  },

  computed: {
    ...mapGetters([
      "isOwner",
      "country",
      "products",
      "currentPlan",
      "currentQuotas",
      "currentTenant",
      "selectedOrder",
      "selectedOrderSession",
      "selectedForwardedOrder",
      "forwardedOrderForm",
      "orderForwardedOrders",
      "tenantShippingAddresses"
    ]),

    lineItem() {
      const product = this.products.find(
        (p) => p.product_code === this.foParams.product
      )
      const quota = this.currentQuotas.find(
        (q) => q.product_code === this.foParams.product
      )
      let tax_amount = null

      if (this.acquisitionStrategy)
        tax_amount = this.acquisitionStrategy[0].entity_metadata?.tax?.amount
      return {
        description: this.foParams.description,
        quantity: 1,
        currency: this.currentPlan?.subscription_currency,
        price: this.acquisitionStrategy
          ? this.acquisitionStrategy[0].price
          : product?.price,
        product_code: product.product_code,
        quota_code: quota.quota_code,
        tax_amount: tax_amount
      }
    },

    acquisitionMethod() {
      return this.acquisitionStrategy?.find(
        (s) => s.product_code === this.foParams.product
      ).method
    },

    designsAvailable() {
      const designs = this.currentQuotas.find(
        (q) => q.product_code === this.foParams.product
      )
      return designs.current
    },

    checkImplantsError() {
      if (this.foParams.product === "DESIGN") {
        if (!this.localForwardedOrderForm.order_form.connection_type) {
          return null
        }
        for (const implant of this.localForwardedOrderForm.implants) {
          if (
            implant.implant_platform.to_tibase &&
            this.localForwardedOrderForm.order_form.connection_type !==
              "ti_base_to_mua"
          ) {
            return "For a Direct to MUA Connection, please select MUAs without the flag 'To Ti-Base'"
          }
          if (
            !implant.implant_platform.to_tibase &&
            this.localForwardedOrderForm.order_form.connection_type ===
              "ti_base_to_mua"
          ) {
            return "For a Ti-Base to MUA Connection Type, please select MUAs with the flag 'To Ti-Base'."
          }
          if (
            !implant.implant_platform.to_tibase &&
            this.localForwardedOrderForm.order_form.connection_type ===
              "ti_base_to_mua"
          ) {
            return "For a Ti-Base to MUA Connection Type, please select MUAs with the flag 'To Ti-Base'."
          }
        }
      } else {
        if (!this.localForwardedOrderForm.implants.length)
          return "Please select at least one tooth"

        for (const implant of this.localForwardedOrderForm.implants) {
          if (implant.implant_platform.to_tibase) {
            return `${this.foParams.product} does not support 'To Ti-Base' MUA. To continue, please select MUAs without 'To Ti-Base'`
          }
        }
      }
      return null
    },

    addons() {
      let addons = []
      if (
        this.foParams.product === "BAR" &&
        this.localForwardedOrderForm?.order_form?.bar_high_shine
      )
        addons.push("HIGH-SHINE")
      return addons
    }
  },

  methods: {
    ...mapActions([
      "triggerReload",
      "setSelectedForwardedOrder",
      "setOrderForwardedOrders",
      "setErrorMessage"
    ]),

    async handleContinue() {
      if (!this.$refs.orderForm.validate()) {
        this.scrollToFirstInvalidField(this.$refs.orderForm)
        return
      }
      this.loading = true
      const shippingAddressUid = this.tenantShippingAddresses.find(
        (addr) => addr.uid === this.currentTenant.primary_shipping_address_uid
      ).uid
      await this.loadAcquisitionStrategy(shippingAddressUid)
      this.loading = false
      this.dialog = true
    },

    async loadAcquisitionStrategy(shippingAddressUid) {
      this.loading = true
      const purchaseForm = {
        items: [
          {
            product_code: this.foParams.product,
            quantity: 1,
            addons: this.addons
          }
        ],
        shipping_address_uid: shippingAddressUid,
        order_uid: this.selectedOrder.uid
      }
      if (this.currentPlan.dso_enabled && this.selectedOrder.client.is_dso)
        purchaseForm.conditions = { dso_end_client: true }

      const strategyCheckResponse = await client.gateway.billing.checkStrategy(
        purchaseForm
      )
      this.acquisitionStrategy = strategyCheckResponse.strategy
      this.totalPriceCents = strategyCheckResponse.total_price_cents
      this.totalTaxAmount = strategyCheckResponse.total_tax_amount
      this.taxInclusive = strategyCheckResponse.tax_inclusive
      this.customerBalance = strategyCheckResponse.current_balance

      this.loading = false
    },

    async handleConfirm(paymentMethodId, shippingAddressUid) {
      this.loading = true
      const purchaseForm = {
        items: [
          {
            product_code: this.foParams.product,
            quantity: 1,
            addons: this.addons
          }
        ],
        payment_id: paymentMethodId,
        order_uid: this.selectedOrder.uid,
        order_session_uid: this.selectedOrderSession.uid,
        shipping_address_uid: shippingAddressUid,
        metadata: {
          forwarded_order_uid: this.selectedForwardedOrder.uid,
          product_code: this.foParams.product,
          end_client_postal_code: this.selectedOrder.client?.post_code,
          dispatched: "False"
        }
      }
      if (this.currentPlan.dso_enabled && this.selectedOrder.client.is_dso)
        purchaseForm.conditions = { dso_end_client: true }
      try {
        await client.gateway.billing.commitPurchase(purchaseForm)
      } catch (error) {
        this.setErrorMessage(
          `Failed to order ${this.foParams.description || "product"}. ${
            error?.response?.data?.detail?.[0] || ""
          }`
        )
        this.loading = false
        this.dialog = false
        return
      }

      if (this.acquisitionMethod === "quota") {
        this.triggerReload("quotas")
      }

      if (this.foParams.caseType === "handover") {
        await this.checkReady("oep_submission_uid")

        window.open(
          `${process.env.VUE_APP_NEXUS_ORDERING_URL}/${this.selectedForwardedOrder.oep_submission_uid}`,
          "_blank"
        )
      } else {
        await this.checkReady("submitted_at")
      }
    },

    async checkReady(item) {
      if (this.retryCount === 10) {
        this.loading = false
        this.dialog = false
        this.retryCount = 0
        return
      }
      if (this.selectedForwardedOrder[item]) {
        this.triggerReload("forwardedOrder")
        this.triggerReload("forwardedOrders")
        this.triggerReload("order")
        this.loading = false
        this.dialog = false
      } else {
        this.retryCount++
        this.triggerReload("forwardedOrder")
        await new Promise((resolve) => setTimeout(resolve, 1000))
        return this.checkReady()
      }
    },

    scrollToFirstInvalidField(element) {
      const children = element?.$children
      if (!children || !children?.length) return

      for (let child of children) {
        if (
          (Object.keys(child).includes("valid") && !child.valid) ||
          (Object.keys(child).includes("error") && child.error)
        ) {
          this.$nextTick(() => {
            const element = child.$el
            element.scrollIntoView({ behavior: "smooth", block: "center" })
          })
          return null
        }
        if (this.scrollToFirstInvalidField(child) === null) return null
      }
    }
  },

  beforeMount() {
    this.localForwardedOrderForm = deepCopy(this.forwardedOrderForm)
    if (this.selectedOrderSession.is_photogrammetry)
      this.localForwardedOrderForm.order_form.photogrammetry_case = "yes"
    if (this.selectedOrderSession.reference_number)
      this.localForwardedOrderForm.order_form.analyzer_reference =
        this.selectedOrderSession.reference_number
  }
}
</script>
